import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Filet de sécurité contre les écrans blancs.
 *
 * Sans ce garde-fou, la moindre erreur levée au rendu d'une page démonte tout
 * l'arbre React → écran blanc total. On capture l'erreur via `componentDidCatch`
 * et on affiche une carte récupérable (bouton « Réessayer » qui reset l'état,
 * bouton « Retour au tableau de bord »).
 *
 * En mode développement (`import.meta.env.DEV`), on affiche le message et la
 * stack pour faciliter le diagnostic ; en production, message neutre.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  info: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, info: null };
  }

  public static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    // En dev, on garde une trace console exploitable. En prod, ce point
    // pourra être branché sur un reporter (Sentry…) ; pas de console.log.
    this.setState({ error, info });
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info.componentStack);
    }
  }

  private readonly handleReset = (): void => {
    this.setState({ error: null, info: null });
  };

  private readonly handleHome = (): void => {
    // Reset puis navigation dure vers le tableau de bord : robuste même si le
    // routeur lui-même est dans un état incohérent.
    this.setState({ error: null, info: null });
    window.location.assign('/dashboard');
  };

  public override render(): ReactNode {
    const { error, info } = this.state;
    if (error === null) {
      return this.props.children;
    }

    return (
      <div className="grid min-h-[60vh] place-items-center px-4 py-10">
        <div
          role="alert"
          className="w-full max-w-lg rounded-card border border-line bg-white p-8 shadow-lift"
        >
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-danger/10 text-danger">
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
              <path
                d="M12 9v4m0 4h.01M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h2 className="mt-4 font-display text-lg font-extrabold tracking-tightish text-navy-700">
            Une erreur est survenue
          </h2>
          <p className="mt-1 font-sans text-sm text-sub">
            Cette section n&apos;a pas pu s&apos;afficher correctement. Vous pouvez réessayer ou
            revenir au tableau de bord.
          </p>

          {import.meta.env.DEV && (
            <pre className="mt-4 max-h-60 overflow-auto rounded-xl border border-danger/30 bg-danger/5 p-3 font-mono text-[11px] leading-relaxed text-danger">
              {error.message}
              {info?.componentStack ?? ''}
            </pre>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={this.handleReset}
              className="rounded-xl bg-gold-500 px-4 py-2.5 font-display text-xs font-extrabold text-navy-700 shadow-gold hover:bg-gold-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
            >
              Réessayer
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="rounded-xl border border-line bg-white px-4 py-2.5 font-display text-xs font-bold text-navy-700 hover:border-gold-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
            >
              Retour au tableau de bord
            </button>
          </div>
        </div>
      </div>
    );
  }
}
